/**
 * When an event stops being visible.
 *
 * The rule: an event is shown until the end of the day it finishes, measured in
 * Pacific time. A single-day event finishes on its `date`; a multi-day one
 * finishes on its `end_date`. Nothing is deleted — these helpers only decide
 * what a reader is shown.
 *
 * Two things this replaces:
 *
 * - `new Date().toISOString().split('T')[0]`, which is today in UTC. For a
 *   reader in Los Angeles that rolls over at 4pm or 5pm their time, so an
 *   event vanished most of a day early.
 * - `new Date(new Date().toDateString())`, which is midnight on the viewer's
 *   own device. That gave a different answer per reader depending on where
 *   they were sitting.
 *
 * `profile_events.date` is a `date` column and `end_date` is a nullable `date`,
 * so both come back as plain "YYYY-MM-DD" strings. Comparing those as strings
 * is exact — no Date object, no parsing, no timezone applied twice.
 */

const EVENT_TIME_ZONE = 'America/Los_Angeles';

/**
 * Today's date in Pacific time as "YYYY-MM-DD".
 *
 * en-CA formats as YYYY-MM-DD, and naming the IANA zone means the switch
 * between PST and PDT is handled for us rather than hardcoding an offset.
 */
export function todayInPacific(now = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: EVENT_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

/**
 * The last day an event is visible: its end_date if it has one, otherwise its
 * start date. Returns null when neither is set.
 */
export function lastVisibleDay(event) {
  if (!event) return null;
  const end   = event.end_date || event.endDate || null;
  const start = event.date     || event.event_date || null;
  return end || start || null;
}

/**
 * Is this event still worth showing?
 *
 * An event with no date is hidden. The two filters that existed before this
 * disagreed on that — ShareButton kept them, the Community query dropped them
 * because `date >= today` is never true for a null — and a row with no date is
 * an import that did not parse rather than an event anyone can attend.
 *
 * Flip this one constant if that judgement turns out to be wrong; nothing else
 * needs to change.
 */
const SHOW_EVENTS_WITH_NO_DATE = false;

export function isUpcoming(event, today = todayInPacific()) {
  const day = lastVisibleDay(event);
  if (!day) return SHOW_EVENTS_WITH_NO_DATE;
  return day >= today;
}

/** Drop the events that have finished. */
export function upcomingOnly(events, today = todayInPacific()) {
  return (events || []).filter(e => isUpcoming(e, today));
}

/**
 * Add the same rule to a Supabase query, so past events are never sent to the
 * browser in the first place.
 *
 * PostgREST has no coalesce in a filter, so this is spelled out: keep the row
 * if end_date is today or later, or — when there is no end_date — if date is
 * today or later. Rows with neither are kept by the third clause, matching
 * isUpcoming().
 *
 * THIS RULE IS ALSO WRITTEN IN SQL, and the two must agree.
 * `event_country_counts()` and `event_region_counts()`
 * (supabase/migrations/20260918000000_event_locations.sql) count the events
 * behind the browse-by-location screens, and they spell the same rule as
 * `coalesce(end_date, date) >= (now() at time zone 'America/Los_Angeles')::date`
 * — which reduces to exactly the two clauses below: `end_date >= today` when
 * there is an end_date, `date >= today` when there is not.
 *
 * If they drift, a country chip reads "12" and the list behind it shows 9,
 * and nothing anywhere reports a problem. Change one, change the other.
 */
export function onlyUpcoming(query, today = todayInPacific()) {
  const clauses = [
    `end_date.gte.${today}`,
    `and(end_date.is.null,date.gte.${today})`,
  ];
  if (SHOW_EVENTS_WITH_NO_DATE) clauses.push('and(end_date.is.null,date.is.null)');
  return query.or(clauses.join(','));
}

/**
 * Format an event's date for display.
 *
 * Always parsed at noon. A "YYYY-MM-DD" string with a time appended is read as
 * local time, and midnight is the one instant that can fail to exist: on a
 * spring-forward day in the zones that skip it, "T00:00:00" rolls to the
 * previous day and the card shows the wrong date. Noon has no such edge.
 *
 * This file had both spellings — 'T00:00:00' in two places and 'T12:00:00' in
 * two others, on the same field in the same component — which is why the same
 * event could render as two different days depending on which card you looked
 * at.
 */
export function formatEventDate(dateStr, options = { month: 'short', day: 'numeric' }, fallback = 'TBD') {
  if (!dateStr) return fallback;
  const parsed = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return fallback;
  return parsed.toLocaleDateString('en-US', options);
}

/** The day of the month, for the little calendar tiles. */
export function eventDayOfMonth(dateStr) {
  if (!dateStr) return '';
  const parsed = new Date(`${dateStr}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? '' : String(parsed.getDate());
}
